import { create } from 'zustand'

import { initialState } from './state'
import { Setters, State, TListingSource, TWiggotUserType } from './types'

import {
  EOpportunityCriteria,
  EPropertyType,
  ESearchLevel,
} from '../../../../types-library/src'

export const useFilterStore = create<State & Setters>((set) => ({
  ...initialState,
  setConstructionType: (constructionType: string | undefined) =>
    set(() => ({ constructionType })),
  setPresale: (presale: boolean) => set(() => ({ presale })),
  setPriceFrom: (priceFrom: number) => set(() => ({ priceFrom })),
  setPriceTo: (priceTo: number) => set(() => ({ priceTo })),
  setRooms: (rooms: number) => set(() => ({ rooms })),
  setBathrooms: (bathrooms: number) => set(() => ({ bathrooms })),
  setParkings: (parkings: number) => set(() => ({ parkings })),
  setPropertyType: (propertyType: EPropertyType) =>
    set(() => ({
      propertyType,
      opportunityCriteria: undefined,
      opportunityCriteriaReadOnly: undefined,
      priceFrom: 0,
      priceTo: 0,
    })),
  setPropertyTypeWithOpportunity: (propertyType: EPropertyType) =>
    set(() => ({ propertyType })),
  setOpportunityCriteria: (
    opportunityCriteria: EOpportunityCriteria | undefined
  ) => set(() => ({ opportunityCriteria })),
  setLatitude: (latitude: number) => set(() => ({ latitude })),
  setLongitude: (longitude: number) => set(() => ({ longitude })),

  setSearchLevel: (searchLevel: ESearchLevel) => set(() => ({ searchLevel })),
  setSearchAddress: (searchAddress: string | undefined) =>
    set(() => ({ searchAddress })),

  setIsSidePanelOpened: (isSidePanelOpened: boolean) =>
    set(() => ({ isSidePanelOpened })),

  setMaxRegisteredPrice: (maxRegisteredPrice: number) => {
    set(() => ({ maxRegisteredPrice }))
  },
  setMinRegisteredPrice: (minRegisteredPrice: number) => {
    set(() => ({ minRegisteredPrice }))
  },
  setOpportunityCriteriaReadOnly: (
    opportunityCriteriaReadOnly: EOpportunityCriteria | undefined
  ) => {
    set(() => ({ opportunityCriteriaReadOnly }))
  },
  setPublishedDate: (publishedDate: string | undefined) =>
    set(() => ({ publishedDate })),
  setFailedOpportunityCriterion: (failedOpportunityCriterion: boolean) => {
    set(() => ({ failedOpportunityCriterion }))
  },
  setAmenities: (amenities: string[]) => set(() => ({ amenities })),
  setAreaBounds: (areaBounds: number[][]) => set(() => ({ areaBounds })),
  setSelectedPolygonId: (selectedPolygonId: string | undefined) => {
    set(() => ({ selectedPolygonId }))
  },
  setPriceDiffFrom: (priceDiffFrom: number) => set(() => ({ priceDiffFrom })),
  setPriceDiffTo: (priceDiffTo: number) => set(() => ({ priceDiffTo })),
  setCapRateFrom: (capRateFrom: number) => set(() => ({ capRateFrom })),
  setCapRateTo: (capRateTo: number) => set(() => ({ capRateTo })),
  setPriceBySqmFrom: (priceBySqmFrom: number) =>
    set(() => ({ priceBySqmFrom })),
  setPriceBySqmTo: (priceBySqmTo: number) => set(() => ({ priceBySqmTo })),
  setConstructionYearFrom: (constructionYearFrom) =>
    set(() => ({ constructionYearFrom })),
  setConstructionYearTo: (constructionYearTo) =>
    set(() => ({ constructionYearTo })),
  setCapRateFilterEnabled: (capRateFilterEnabled: boolean) =>
    set(() => ({ capRateFilterEnabled })),
  setPriceDiffFilterEnabled: (priceDiffFilterEnabled: boolean) =>
    set(() => ({ priceDiffFilterEnabled })),
  setPriceBySqmFilterEnabled: (priceBySqmFilterEnabled: boolean) =>
    set(() => ({ priceBySqmFilterEnabled })),
  setPamRequestId: (pamRequestId: string | undefined) =>
    set(() => ({ pamRequestId })),
  setPropertyOrder: (propertyOrder) => set(() => ({ propertyOrder })),
  setOrigin: (origin: string) => set(() => ({ origin })),
  setShowOnlyCommissionProperties: (showOnlyCommissionProperties: boolean) =>
    set(() => ({ showOnlyCommissionProperties })),
  setAssociationIds: (associationIds: string | undefined) => {
    set(() => ({ associationIds }))
  },
  setAgencyId: (agencyId: number | undefined) => {
    set(() => ({ agencyId }))
  },
  setCommissionRateLowRange: (commissionRateLowRange: number | undefined) =>
    set(() => ({ commissionRateLowRange })),
  setCommissionRateHighRange: (commissionRateHighRange: number | undefined) =>
    set(() => ({ commissionRateHighRange })),
  setSurfaceFrom: (surfaceFrom: number | undefined) =>
    set(() => ({ surfaceFrom })),
  setSurfaceTo: (surfaceTo: number | undefined) => set(() => ({ surfaceTo })),
  setListingsSource: (listingsSource: TListingSource) =>
    set(() => ({ listingsSource })),
  setWiggotUserType: (wiggotUserType: TWiggotUserType) =>
    set(() => ({ wiggotUserType })),
  setLocations: (locations) => set(() => ({ locations })),
  resetFilterByKey: (key: string) => {
    switch (key) {
      case 'priceFrom':
        return set(() => ({ priceFrom: 0 }))
      case 'priceTo':
        return set(() => ({ priceTo: 0 }))
      case 'rooms':
        return set(() => ({ rooms: 0 }))
      case 'bathrooms':
        return set(() => ({ bathrooms: 0 }))
      case 'parkings':
        return set(() => ({ parkings: 0 }))
      case 'latitude':
        return set(() => ({ latitude: 0 }))
      case 'longitude':
        return set(() => ({ longitude: 0 }))
      case 'priceDiffFrom':
        return set(() => ({ priceDiffFrom: 0 }))
      case 'priceDiffTo':
        return set(() => ({ priceDiffTo: 0 }))
      case 'capRateFrom':
        return set(() => ({ capRateFrom: 1 }))
      case 'capRateTo':
        return set(() => ({ capRateTo: 1 }))
      case 'priceBySqmFrom':
        return set(() => ({ priceBySqmFrom: 0 }))
      case 'priceBySqmTO':
        return set(() => ({ priceBySqmTo: 0 }))
      case 'constructionYearFrom':
        return set(() => ({ constructionYearFrom: 0 }))
      case 'constructionYearTo':
        return set(() => ({ constructionYearTo: 0 }))
      case 'amenities':
        return set((state) => {
          if (state.amenities.length === 0) {
            return { amenities: state.amenities }
          } else {
            return { amenities: [] }
          }
        })
      case 'propertyType':
        return set(() => ({ propertyType: EPropertyType.SALE }))
      case 'opportunityCriteria':
        return set(() => ({ opportunityCriteria: undefined }))
      case 'constructionType':
        return set(() => ({ constructionType: undefined }))
      case 'presale':
        return set(() => ({ presale: false }))
      case 'searchLevel':
        return set(() => ({ searchLevel: ESearchLevel.STATE }))
      case 'searchAddress':
        return set(() => ({ searchAddress: '' }))
      case 'publishedDate':
        return set(() => ({ publishedDate: undefined }))
      default:
        return set((state) => state)
    }
  },
}))
